<template>
  <div class="w-full h-full flex flex-col">
    <!-- Header con info contextual -->
    <div class="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between">
      <div>
        <h1 class="text-lg font-semibold text-gray-800">
          {{ mode === 'restore' ? 'Restaurar versión anterior' : 'Confirmar nueva versión' }}
        </h1>
        <p class="text-xs text-gray-500 mt-1">
          {{ pictogram?.name }}
          <template v-if="mode === 'restore' && fromVersion">
            · restaurando v{{ fromVersion }}
          </template>
        </p>
      </div>
      <b-button type="is-light" size="is-small" icon-pack="fas" icon-left="arrow-left"
                :disabled="isConfirming" @click="_OnClickBack">
        Volver
      </b-button>
    </div>

    <!-- Loading inicial: pidiendo el preview al backend -->
    <div v-if="isLoadingPreview" class="flex-1 flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large" type="is-primary"></b-icon>
        <p class="mt-4 text-sm text-gray-600">Calculando diferencias con la versión actual...</p>
      </div>
    </div>

    <!-- Error al cargar preview -->
    <div v-else-if="previewError" class="flex-1 flex items-center justify-center bg-gray-50">
      <div class="text-center max-w-md">
        <b-icon pack="fas" icon="exclamation-triangle" size="is-large" type="is-danger"></b-icon>
        <p class="mt-4 text-base font-semibold text-gray-800">No se pudo cargar la comparación</p>
        <p class="mt-2 text-sm text-gray-600">{{ previewError }}</p>
        <b-button class="mt-4" size="is-small" type="is-primary" icon-pack="fas" icon-left="redo"
                  @click="_LoadPreview">
          Reintentar
        </b-button>
      </div>
    </div>

    <!-- Vista del preview: split B — viewer izquierda 70%, datos derecha 30% -->
    <div v-else class="flex-1 flex bg-gray-50 overflow-hidden">

      <!-- Columna izquierda: viewer + overlay + toggles flotantes -->
      <div class="relative flex-1 bg-white border-r border-gray-200" style="min-width: 0;">
        <Viewer
          v-if="candidateCadJson"
          ref="viewer"
          :cad-json="candidateCadJson"
          class="absolute inset-0"
          @epc-loaded="_OnViewerLoaded"
        />

        <DxfDiffOverlay
          v-if="viewerReady && candidateCadJson"
          :viewer="$refs.viewer"
          :diff="previewChanges"
          :show-added="showAdded"
          :show-removed="showRemoved"
          :show-moved="showMoved"
        />

        <!-- Estado loading del scene -->
        <div v-if="isLoadingScene" class="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div class="text-center">
            <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large" type="is-primary"></b-icon>
            <p class="mt-3 text-sm text-gray-600">Cargando vista del nuevo DXF…</p>
          </div>
        </div>

        <!-- Estado error del scene -->
        <div v-else-if="sceneError" class="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div class="text-center max-w-md p-4">
            <b-icon pack="fas" icon="exclamation-triangle" size="is-medium" type="is-warning"></b-icon>
            <p class="mt-3 text-sm text-gray-700">{{ sceneError }}</p>
            <p class="mt-2 text-xs text-gray-500">Los datos del diff siguen visibles en la columna derecha.</p>
          </div>
        </div>

        <!-- Toggles flotantes sobre el viewer (overlay categorías) -->
        <div v-if="viewerReady" class="absolute top-3 left-3 bg-white/90 backdrop-blur rounded-md border border-gray-200 shadow-sm p-2 space-y-1">
          <p class="text-[10px] font-semibold uppercase text-gray-500 px-1 pb-1 border-b border-gray-200">Mostrar</p>
          <label class="flex items-center gap-2 px-1 py-0.5 text-xs cursor-pointer">
            <input type="checkbox" v-model="showAdded" class="form-checkbox text-green-600" />
            <span class="inline-block w-2 h-2 rounded-sm bg-green-500"></span>
            <span>Nuevas ({{ previewTotals.added }})</span>
          </label>
          <label class="flex items-center gap-2 px-1 py-0.5 text-xs cursor-pointer">
            <input type="checkbox" v-model="showMoved" class="form-checkbox text-amber-600" />
            <span class="inline-block w-2 h-2 rounded-sm bg-amber-500"></span>
            <span>Movidas ({{ previewTotals.moved }})</span>
          </label>
          <label class="flex items-center gap-2 px-1 py-0.5 text-xs cursor-pointer">
            <input type="checkbox" v-model="showRemoved" class="form-checkbox text-red-600" />
            <span class="inline-block w-2 h-2 rounded-sm bg-red-500"></span>
            <span>Eliminadas ({{ previewTotals.removed }})</span>
          </label>
        </div>
      </div>

      <!-- Columna derecha: totales + listas + acciones -->
      <div class="w-[360px] flex-shrink-0 overflow-y-auto bg-gray-50 p-4 space-y-4">

        <!-- Totales -->
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <h2 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumen del diff</h2>
          <div class="grid grid-cols-2 gap-2">
            <div class="text-center p-2 rounded-md border border-green-200 bg-green-50">
              <p class="text-[10px] font-semibold uppercase text-green-700">Nuevas</p>
              <p class="text-2xl font-bold text-green-800 mt-1">{{ previewTotals.added }}</p>
            </div>
            <div class="text-center p-2 rounded-md border border-amber-200 bg-amber-50">
              <p class="text-[10px] font-semibold uppercase text-amber-700">Movidas</p>
              <p class="text-2xl font-bold text-amber-800 mt-1">{{ previewTotals.moved }}</p>
            </div>
            <div class="text-center p-2 rounded-md border border-red-200 bg-red-50">
              <p class="text-[10px] font-semibold uppercase text-red-700">Eliminadas</p>
              <p class="text-2xl font-bold text-red-800 mt-1">{{ previewTotals.removed }}</p>
            </div>
            <div class="text-center p-2 rounded-md border border-gray-200 bg-white">
              <p class="text-[10px] font-semibold uppercase text-gray-600">Sin cambios</p>
              <p class="text-2xl font-bold text-gray-700 mt-1">{{ previewTotals.unchanged }}</p>
            </div>
          </div>
          <p v-if="previewExpiresAt" class="text-[10px] text-gray-400 mt-3 text-center">
            Válido hasta {{ _FormatTime(previewExpiresAt) }}
          </p>
        </div>

        <!-- Listas colapsables -->
        <details v-if="previewChanges.added?.length" class="bg-white rounded-lg border border-gray-200 p-3">
          <summary class="cursor-pointer text-xs font-semibold text-green-700 flex items-center gap-2">
            <b-icon pack="fas" icon="plus-circle" size="is-small"></b-icon>
            Nuevas ({{ previewChanges.added.length }})
          </summary>
          <ul class="text-[11px] mt-2 ml-1 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
            <li v-for="(it, i) in previewChanges.added" :key="'a'+i">
              {{ it.name || it.handle || '(sin nombre)' }}
            </li>
          </ul>
        </details>

        <details v-if="previewChanges.moved?.length" class="bg-white rounded-lg border border-gray-200 p-3">
          <summary class="cursor-pointer text-xs font-semibold text-amber-700 flex items-center gap-2">
            <b-icon pack="fas" icon="arrows-alt" size="is-small"></b-icon>
            Movidas ({{ previewChanges.moved.length }})
          </summary>
          <ul class="text-[11px] mt-2 ml-1 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
            <li v-for="(it, i) in previewChanges.moved" :key="'m'+i">
              {{ it.name || it.handle || '(sin nombre)' }}
            </li>
          </ul>
        </details>

        <details v-if="previewChanges.removed?.length" class="bg-white rounded-lg border border-gray-200 p-3">
          <summary class="cursor-pointer text-xs font-semibold text-red-700 flex items-center gap-2">
            <b-icon pack="fas" icon="minus-circle" size="is-small"></b-icon>
            Eliminadas ({{ previewChanges.removed.length }})
          </summary>
          <ul class="text-[11px] mt-2 ml-1 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
            <li v-for="(it, i) in previewChanges.removed" :key="'r'+i">
              {{ it.name || it.handle || '(sin nombre)' }}
            </li>
          </ul>
        </details>

        <!-- Acciones -->
        <div class="bg-white rounded-lg border border-gray-200 p-3 flex justify-end gap-2 sticky bottom-0">
          <b-button type="is-light" size="is-small" :disabled="isConfirming" @click="_OnClickCancel">
            Cancelar
          </b-button>
          <b-button type="is-success" size="is-small" icon-pack="fas" icon-left="check"
                    :loading="isConfirming" :disabled="isConfirming" @click="_OnClickConfirm">
            Confirmar
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  previewVersionUpload,
  confirmVersionUpload,
  getPendingVersionDxfUrl,
} from '../services/pictograms.service';
import { parseCadFile } from '../layout-builder/services/cad-file.service';
import Viewer from '../viewer/Viewer.vue';
import DxfDiffOverlay from './DxfDiffOverlay.vue';

export default {
  name: 'UpdatePictogramFlow',

  components: { Viewer, DxfDiffOverlay },

  props: {
    pictogram: { type: Object, required: true },
    tempKey: { type: String, default: null },
    docVersionId: { type: Number, default: null },
    fromVersion: { type: Number, default: null },
    mode: {
      type: String,
      required: true,
      validator: (v) => ['review', 'restore'].includes(v),
    },
  },

  data() {
    return {
      isLoadingPreview: false,
      isConfirming: false,
      previewError: null,

      previewToken: null,
      previewTotals: { added: 0, removed: 0, moved: 0, unchanged: 0 },
      previewChanges: { added: [], removed: [], moved: [] },
      previewExpiresAt: null,

      // Viewer + overlay
      candidateCadJson: null,
      isLoadingScene:   false,
      sceneError:       null,
      viewerReady:      false,

      // Toggles del overlay — todos on por defecto (no se persiste)
      showAdded:   true,
      showRemoved: true,
      showMoved:   true,
    };
  },

  created() {
    this._LoadPreview();
    this._LoadCandidateScene();
  },

  methods: {
    async _LoadPreview() {
      // El modo 'restore' antiguo (sin tempKey, sólo fromVersion en route param)
      // fue reemplazado: ahora el botón Restaurar arranca el flow server-side
      // que ya devuelve tempKey + docVersionId, así que cae en modo 'review'.
      // Si alguien aterriza acá vía URL directa con sólo fromVersion mostramos
      // mensaje claro en lugar del genérico "faltan datos".
      if (this.mode === 'restore' && !this.tempKey) {
        this.previewError = 'Para restaurar una versión usá el botón "Restaurar" desde el listado de versiones del pictograma.';
        return;
      }

      if (!this.tempKey || !this.docVersionId) {
        this.previewError = 'Faltan datos del upload (temp_key o doc_version_id).';
        return;
      }

      this.isLoadingPreview = true;
      this.previewError = null;

      try {
        const response = await previewVersionUpload(this.pictogram.id, {
          aws_response: {
            key: this.tempKey,
            name: 'scene.json',
            content_type: 'application/json',
          },
          document_version_id: this.docVersionId,
        });

        if (!response.success) {
          throw new Error(response.message || 'El backend rechazó el preview.');
        }

        this.previewToken     = response.preview_token;
        this.previewTotals    = response.totals;
        this.previewChanges   = response.changes;
        this.previewExpiresAt = response.expires_at;
      } catch (err) {
        console.error('previewVersionUpload error:', err);
        this.previewError = err.error_msg || err.message || 'Error inesperado.';
      } finally {
        this.isLoadingPreview = false;
      }
    },

    async _OnClickConfirm() {
      if (!this.previewToken) return;

      this.isConfirming = true;

      try {
        const response = await confirmVersionUpload(this.pictogram.id, {
          preview_token: this.previewToken,
        });

        if (!response.success) {
          throw new Error(response.message || 'El backend rechazó el confirm.');
        }

        const s = response.stats || {};
        const summary = `v${response.new_version} aplicada · `
          + `${s.created_structures || 0} nuevas, `
          + `${s.updated_structures || 0} movidas, `
          + `${s.archived_structures || 0} eliminadas`;

        this.$toast.open({
          message:  summary,
          type:     'is-success',
          position: 'is-top-right',
          duration: 5000,
        });

        // Volver al config del pictograma
        this.$router.push({
          name: 'project.admin.pictogram.config',
          params: {
            project_id: this.pictogram.project_id,
            id:         this.pictogram.id,
          },
        });
      } catch (err) {
        console.error('confirmVersionUpload error:', err);
        this.$toast.open({
          message:  err.error_msg || err.message || 'Error al confirmar.',
          type:     'is-danger',
          position: 'is-top-right',
          duration: 6000,
        });
        this.isConfirming = false;
      }
    },

    _OnClickCancel() {
      this.$router.back();
    },

    _OnClickBack() {
      this.$router.back();
    },

    async _LoadCandidateScene() {
      if (this.mode === 'restore' && !this.tempKey) return;
      if (!this.tempKey) return;

      this.isLoadingScene = true;
      this.sceneError = null;

      try {
        // 1. Obtener signed URL del DXF de la versión pendiente.
        // El scene snapshot que se guarda en S3 tiene formato pensado para el
        // diff backend (entities indexed by id) — no sirve para el Viewer.
        // Por eso descargamos el DXF original y lo parseamos en cliente con
        // el mismo `parseCadFile` que usa LayoutBuilder en el upload normal.
        let urlRes;
        try {
          urlRes = await getPendingVersionDxfUrl(this.pictogram.id);
        } catch (apiErr) {
          // apiCall solo procesa 422 — para 404/500 viene el AxiosError crudo.
          const backend = apiErr?.response?.data;
          const errCode = backend?.error || apiErr?.response?.status;
          const errMsg  = backend?.message || apiErr.message || 'No se obtuvo la URL del DXF.';
          console.error('LoadCandidateScene API error:', { errCode, backend, apiErr });
          throw new Error(`${errMsg} (${errCode})`);
        }
        if (!urlRes?.success || !urlRes?.url) {
          throw new Error(urlRes?.message || 'No se obtuvo la URL del DXF.');
        }

        // 2. Descarga del DXF desde S3.
        // Usamos fetch (no axios) porque axios tiene defaults.headers.common.Authorization
        // con el JWT del usuario. La signed URL ya autentica via X-Amz-Signature;
        // mandar Bearer al mismo tiempo hace que S3 rechace con
        // "Only one auth mechanism allowed".
        const dxfResp = await fetch(urlRes.url, { method: 'GET', credentials: 'omit' });
        if (!dxfResp.ok) {
          throw new Error(`DXF fetch falló: HTTP ${dxfResp.status}`);
        }
        const dxfBlob = await dxfResp.blob();
        const dxfFile = new File(
          [dxfBlob],
          urlRes.original_filename || 'pending.dxf',
          { type: 'application/octet-stream' }
        );

        // 3. Parsear localmente con el mismo flow del LayoutBuilder.
        // El cadJson resultante es el formato nativo del parser que el Viewer
        // Three.js sabe renderizar (no el scene format de CadSceneBuilder).
        const cadJson = await parseCadFile(dxfFile);
        this.candidateCadJson = cadJson;
      } catch (err) {
        console.error('LoadCandidateScene error:', err);
        this.sceneError = err.error_msg || err.message || 'No se pudo cargar la vista del nuevo DXF.';
      } finally {
        this.isLoadingScene = false;
      }
    },

    _OnViewerLoaded() {
      // El viewer disparó @epc-loaded — ya tiene scene + renderer listos.
      // En el siguiente tick el ref está estable y DxfDiffOverlay puede leerlo.
      this.$nextTick(() => {
        this.viewerReady = true;
      });
    },

    _FormatTime(isoStr) {
      if (!isoStr) return '';
      try {
        return new Date(isoStr).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      } catch {
        return isoStr;
      }
    },
  },
};
</script>
